// Follow-ups on the Sentinel Workspace settings page, for the selected brand: who has opted out.

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getApiErrorMessage } from '@/lib/utils';
import { followupsService } from '@/services/followups.service';
import type { FollowupOptOut } from './types';

type BuScope = { bu_id?: string };

const labelClass = 'text-[13px] font-normal leading-[18px] text-text-body';
const helperClass = 'text-[11px] leading-4 text-gray-800';

/** Follow-ups are always on and live; the only brand setup is who has opted out. */
export function FollowupSettingsSection({ buScope, canEdit }: { buScope: BuScope; canEdit: boolean }) {
  const [optOuts, setOptOuts] = useState<FollowupOptOut[] | null>(null);
  const [optOutError, setOptOutError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  // The brand on screen now: a removal that finishes after a brand switch must not land on the new brand.
  const brandRef = useRef(buScope.bu_id);
  brandRef.current = buScope.bu_id;

  // Per brand, like the rest of the page: a stale form here would edit one brand believing it is another.
  useEffect(() => {
    let cancelled = false;
    setOptOuts(null);
    setOptOutError(null);
    followupsService
      .listOptOuts(buScope)
      .then((items) => !cancelled && setOptOuts(items))
      .catch((err) => !cancelled && setOptOutError(getApiErrorMessage(err, 'Could not load opted-out customers.')));
    return () => {
      cancelled = true;
    };
  }, [buScope]);

  const remove = async (customerKey: string) => {
    const brand = buScope.bu_id;
    setRemoving(customerKey);
    setOptOutError(null);
    try {
      await followupsService.removeOptOut(customerKey, buScope);
      if (brandRef.current === brand) setOptOuts((items) => items?.filter((item) => item.customerKey !== customerKey) ?? null);
    } catch (err) {
      if (brandRef.current === brand) setOptOutError(getApiErrorMessage(err, 'Could not remove the opt-out.'));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="flex w-[600px] flex-col gap-3">
      <p className="text-[12px] font-medium leading-4 tracking-wide text-gray-700">Opted-out customers</p>
      <div className="flex flex-col gap-2 rounded-2xl border border-gray-500 bg-white px-4 py-4">
        {optOutError && (
          <p role="alert" className="text-[12px] text-red-600">
            {optOutError}
          </p>
        )}
        {optOuts === null ? (
          !optOutError && <Skeleton className="h-10 w-full rounded-xl" />
        ) : optOuts.length === 0 ? (
          <p className={helperClass}>No customer has opted out. Opt one out from a follow-up's details in the Sentinel Workspace.</p>
        ) : (
          <ul aria-label="Opted-out customers" className="flex flex-col divide-y divide-gray-300">
            {optOuts.map((item) => (
              <li key={item.customerKey} className="flex items-center justify-between gap-4 py-2">
                <div className="flex min-w-0 flex-col">
                  <span className={labelClass}>{item.email ?? item.customerKey}</span>
                  <span className={helperClass}>
                    Since {new Date(item.createdAt).toLocaleDateString()}
                    {item.createdBy ? ` · by ${item.createdBy}` : ''}
                  </span>
                </div>
                {canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={removing === item.customerKey}
                    onClick={() => void remove(item.customerKey)}
                    aria-label={`Remove opt-out for ${item.email ?? item.customerKey}`}
                  >
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
